import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ROLE_LABELS } from '../types/rbacImproved';
import { PagePermissions, ComponentPermissions, ActionPermissions } from '../types/rbacImproved';
import { PermissionService } from '../utils/permissions';
import { BaseModal, SectionTitle, PrimaryButton, SecondaryButton, } from './shared/BaseModal';
const Section = styled.div `
  margin-bottom: 28px;

  .description {
    color: var(--text-secondary);
    font-size: 14px;
    margin-bottom: 16px;
  }
`;
const RoleSelector = styled.select `
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 14px 16px;
  font-size: 14px;
  width: 100%;
  max-width: 300px;
  transition: all 0.15s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--accent-gold);
    box-shadow: 0 0 0 3px var(--accent-gold-bg);
  }

  option {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }
`;
const PermissionGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
`;
const PermissionGroup = styled.div `
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold), var(--accent-gold-light));
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover::before {
    opacity: 1;
  }

  h4 {
    color: var(--text-primary);
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;
const PermissionItem = styled.label `
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 6px 8px;
  margin-left: -8px;
  margin-right: -8px;
  border-radius: 4px;
  transition: background 0.15s ease;

  &:hover {
    background: var(--accent-gold-bg);
  }

  input[type="checkbox"] {
    cursor: pointer;
    accent-color: var(--accent-gold);
  }

  &.inherited {
    color: var(--text-tertiary);

    input[type="checkbox"] {
      cursor: not-allowed;
    }
  }

  .permission-label {
    flex: 1;
    text-transform: capitalize;
  }

  .permission-status {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 3px 8px;
    border-radius: 3px;
  }

  .inherited-status {
    background: rgba(22, 163, 74, 0.08);
    color: #15803D;
    border: 1px solid rgba(22, 163, 74, 0.15);
  }

  .override-status {
    background: var(--accent-gold-bg);
    color: var(--accent-gold);
    border: 1px solid rgba(184, 134, 11, 0.2);
  }

  .revoked-status {
    background: rgba(220, 38, 38, 0.08);
    color: #B91C1C;
    border: 1px solid rgba(220, 38, 38, 0.15);
  }
`;
const CurrentPermissions = styled.div `
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 18px;
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold), var(--accent-gold-light));
  }

  h4 {
    color: var(--text-primary);
    font-family: 'Playfair Display', serif;
    font-size: 16px;
    margin-bottom: 12px;
  }

  .permission-count {
    color: var(--accent-gold);
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 12px;
  }

  .permission-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .permission-tag {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    color: var(--text-primary);
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 500;
    border-radius: 4px;
    text-transform: capitalize;
    transition: all 0.15s ease;

    &:hover {
      border-color: var(--accent-gold);
      background: var(--accent-gold-bg);
    }
  }
`;
const EditPermissionsModal = ({ user, isOpen, onClose, onSave }) => {
    const [selectedRole, setSelectedRole] = useState(user.roleLevel);
    const [additionalPermissions, setAdditionalPermissions] = useState([]);
    const [revokedPermissions, setRevokedPermissions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedRole(user.roleLevel);
            setAdditionalPermissions([]);
            setRevokedPermissions([]);
        }
    }, [isOpen, user.roleLevel]);
    // Get all available permissions
    const allPermissions = [
        ...Object.values(PagePermissions),
        ...Object.values(ComponentPermissions),
        ...Object.values(ActionPermissions)
    ];
    // Get inherited permissions from selected role
    const inheritedPermissions = PermissionService.calculateUserPermissions(selectedRole);
    // Get final permissions (inherited + additional - revoked)
    const finalPermissions = [
        ...inheritedPermissions,
        ...additionalPermissions
    ].filter(p => !revokedPermissions.includes(p));
    const handlePermissionToggle = (permission) => {
        const isInherited = inheritedPermissions.includes(permission);
        const isAdditional = additionalPermissions.includes(permission);
        const isRevoked = revokedPermissions.includes(permission);
        if (isInherited) {
            // If inherited, can only revoke
            if (isRevoked) {
                setRevokedPermissions(prev => prev.filter(p => p !== permission));
            }
            else {
                setRevokedPermissions(prev => [...prev, permission]);
            }
        }
        else {
            // If not inherited, can add as additional
            if (isAdditional) {
                setAdditionalPermissions(prev => prev.filter(p => p !== permission));
            }
            else {
                setAdditionalPermissions(prev => [...prev, permission]);
            }
        }
    };
    const getPermissionStatus = (permission) => {
        const isInherited = inheritedPermissions.includes(permission);
        const isAdditional = additionalPermissions.includes(permission);
        const isRevoked = revokedPermissions.includes(permission);
        if (isInherited && isRevoked)
            return 'revoked';
        if (isInherited)
            return 'inherited';
        if (isAdditional)
            return 'override';
        return 'none';
    };
    const isPermissionActive = (permission) => {
        return finalPermissions.includes(permission);
    };
    const handleSave = async () => {
        setIsLoading(true);
        try {
            await onSave(user.id, {
                roleLevel: selectedRole,
                additionalPermissions,
                revokedPermissions
            });
            onClose();
        }
        catch (error) {
            console.error('Failed to save permissions:', error);
        }
        finally {
            setIsLoading(false);
        }
    };
    // Group permissions by category
    const pagePermissions = Object.values(PagePermissions);
    const componentPermissions = Object.values(ComponentPermissions);
    const actionPermissions = Object.values(ActionPermissions);
    return (_jsx(BaseModal, { isOpen: isOpen, onClose: onClose, title: "Edit Permissions", titleAccent: "Permissions", subtitle: `Manage permissions for ${user.profile.firstName} ${user.profile.lastName}`, size: "lg", footer: _jsxs(_Fragment, { children: [_jsx(SecondaryButton, { onClick: onClose, children: "Cancel" }), _jsx(PrimaryButton, { onClick: handleSave, disabled: isLoading, children: isLoading ? 'Saving...' : 'Save Changes' })] }), children: _jsxs("div", { children: [_jsxs(CurrentPermissions, { children: [_jsx("h4", { children: "Current Permissions Summary" }), _jsxs("div", { className: "permission-count", children: ["Total: ", finalPermissions.length, " permissions"] }), _jsxs("div", { className: "permission-list", children: [finalPermissions.slice(0, 10).map(permission => (_jsx("span", { className: "permission-tag", children: permission.split('.').pop()?.replace(/_/g, ' ') }, permission))), finalPermissions.length > 10 && (_jsxs("span", { className: "permission-tag", children: ["+", finalPermissions.length - 10, " more"] }))] })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "Base Role" }), _jsx("div", { className: "description", children: "Select the base role level. This determines the inherited permissions." }), _jsx(RoleSelector, { value: selectedRole, onChange: (e) => setSelectedRole(Number(e.target.value)), children: Object.entries(ROLE_LABELS).map(([level, label]) => (_jsxs("option", { value: level, children: ["Level ", level, " - ", label] }, level))) })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "Permission Overrides" }), _jsx("div", { className: "description", children: "Grant additional permissions or revoke inherited ones." }), _jsxs(PermissionGrid, { children: [_jsxs(PermissionGroup, { children: [_jsx("h4", { children: "Page Access" }), pagePermissions.map(permission => {
                                            const status = getPermissionStatus(permission);
                                            const isActive = isPermissionActive(permission);
                                            return (_jsxs(PermissionItem, { className: status === 'inherited' ? 'inherited' : '', children: [_jsx("input", { type: "checkbox", checked: isActive, onChange: () => handlePermissionToggle(permission) }), _jsx("span", { className: "permission-label", children: permission.split('.').pop()?.replace(/_/g, ' ') }), status !== 'none' && (_jsx("span", { className: `permission-status ${status}-status`, children: status }))] }, permission));
                                        })] }), _jsxs(PermissionGroup, { children: [_jsx("h4", { children: "Component Access" }), componentPermissions.map(permission => {
                                            const status = getPermissionStatus(permission);
                                            const isActive = isPermissionActive(permission);
                                            return (_jsxs(PermissionItem, { className: status === 'inherited' ? 'inherited' : '', children: [_jsx("input", { type: "checkbox", checked: isActive, onChange: () => handlePermissionToggle(permission) }), _jsx("span", { className: "permission-label", children: permission.split('.').pop()?.replace(/_/g, ' ') }), status !== 'none' && (_jsx("span", { className: `permission-status ${status}-status`, children: status }))] }, permission));
                                        })] }), _jsxs(PermissionGroup, { children: [_jsx("h4", { children: "Actions" }), actionPermissions.map(permission => {
                                            const status = getPermissionStatus(permission);
                                            const isActive = isPermissionActive(permission);
                                            return (_jsxs(PermissionItem, { className: status === 'inherited' ? 'inherited' : '', children: [_jsx("input", { type: "checkbox", checked: isActive, onChange: () => handlePermissionToggle(permission) }), _jsx("span", { className: "permission-label", children: permission.split('.').pop()?.replace(/_/g, ' ') }), status !== 'none' && (_jsx("span", { className: `permission-status ${status}-status`, children: status }))] }, permission));
                                        })] })] })] })] }) }));
};
export default EditPermissionsModal;
